import { api } from "../../api/client";
import type { Athlete, OrgSummary, Page } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { EmptyState, ErrorState, Loading, PageHead, StatusBadge } from "../../components/ui";
import { formatDate } from "../../lib/format";
import { useAsync } from "../../lib/useAsync";
import { Link } from "../../router/router";
import { CoachDashboard } from "./CoachDashboard";
import { t } from "../../i18n";

export function OverviewPage({ orgId }: { orgId: string }) {
  const { me, membership, can } = useAuth();
  const isManager = can("org.update");
  const summary = useAsync(
    () => (isManager ? api.get<OrgSummary>(`/orgs/${orgId}/summary`) : Promise.resolve(null)),
    [orgId, isManager],
  );
  const recent = useAsync(
    () => api.get<Page<Athlete>>(`/orgs/${orgId}/athletes`, { sort: "-created_at", page_size: 6 }),
    [orgId],
  );
  const firstName = me?.full_name.split(" ")[0] ?? "";
  const base = `/org/${orgId}`;

  return (
    <>
      <PageHead
        title={t("Olá, {firstName}", { firstName })}
        subtitle={
          isManager
            ? t("Acompanhamento da {organization_name}.", { organization_name: membership?.organization_name })
            : t("Atletas sob sua responsabilidade e dos grupos que você coordena.")
        }
      />

      {can("performance.read") && <CoachDashboard orgId={orgId} />}

      {isManager && (
        <section className="section" aria-labelledby="cadastro">
          <h2 id="cadastro" style={{ marginBottom: 10 }}>
            {t("Cadastro e operação")}
          </h2>
          {summary.loading && <Loading />}
          {summary.error && <ErrorState error={summary.error} onRetry={summary.reload} />}
          {summary.data && (
            <dl className="scoreboard eight">
              <Metric label={t("Atletas ativos")} value={summary.data.athletes_active} to={`${base}/atletas?status=active`} />
              <Metric label={t("Pausados")} value={summary.data.athletes_paused} to={`${base}/atletas?status=paused`} />
              <Metric label={t("Sem treinador")} value={summary.data.athletes_without_coach} to={`${base}/atletas?sem_treinador=1`} />
              <Metric label={t("Treinadores")} value={summary.data.coaches_active} to={`${base}/treinadores`} />
              <Metric label={t("Grupos")} value={summary.data.groups_active} to={`${base}/grupos`} />
              <Metric label={t("Sem treino publicado na semana")} value={summary.data.athletes_without_published_week} />
              <Metric label={t("Atividades nos últimos 7 dias")} value={summary.data.activities_last_7_days} />
            </dl>
          )}
        </section>
      )}

      <section className="section">
        <h2>{t("Cadastrados recentemente")}</h2>
        {recent.loading && <Loading />}
        {recent.error && <ErrorState error={recent.error} onRetry={recent.reload} />}
        {recent.data && recent.data.items.length === 0 && (
          <div className="panel">
            <EmptyState
              title={isManager ? t("Nenhum atleta cadastrado") : t("Nenhum atleta atribuído a você")}
              action={
                isManager ? (
                  <Link to={`${base}/atletas?novo=1`} className="btn btn-primary">
                    {t("Cadastrar atleta")}
                  </Link>
                ) : undefined
              }
            >
              {isManager
                ? t("Comece pelo cadastro dos atletas; depois atribua treinadores e grupos.")
                : t("Quando a gestão atribuir atletas a você ou a um grupo seu, eles aparecem aqui.")}
            </EmptyState>
          </div>
        )}
        {recent.data && recent.data.items.length > 0 && (
          <div className="table-wrap">
            <table className="data responsive">
              <thead>
                <tr>
                  <th>{t("Atleta")}</th>
                  <th>{t("Treinador")}</th>
                  <th>{t("Grupo")}</th>
                  <th>{t("Ingresso")}</th>
                  <th>{t("Situação")}</th>
                </tr>
              </thead>
              <tbody>
                {recent.data.items.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link className="row-link" to={`${base}/atletas/${a.id}`}>
                        {a.full_name}
                      </Link>
                    </td>
                    <td data-label={t("Treinador")}>{a.coach?.name ?? "—"}</td>
                    <td data-label={t("Grupo")}>{a.group?.name ?? "—"}</td>
                    <td data-label={t("Ingresso")}>{formatDate(a.joined_at)}</td>
                    <td>
                      <StatusBadge status={a.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function Metric({ label, value, to }: { label: string; value: number; to?: string }) {
  return (
    <div className="metric">
      <dt>{to ? <Link to={to}>{label}</Link> : label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
